const ANCHOR_ATTRIBUTE = "data-hint-anchor";

/** Selector of the element a hint points to: the one carrying `data-hint-anchor` with the hint key. */
export const featureHintAnchorSelector = (hintKey: string): string =>
  `[${ANCHOR_ATTRIBUTE}="${hintKey.replace(/["\\]/g, "\\$&")}"]`;

const isRendered = (element: HTMLElement): boolean =>
  typeof element.checkVisibility === "function" ? element.checkVisibility() : true;

/** Resolves the anchor of a hint to a rendered element; hidden or missing anchors resolve to `null`. */
export const resolveFeatureHintAnchor = (hintKey: string): HTMLElement | null => {
  const element = document.querySelector<HTMLElement>(featureHintAnchorSelector(hintKey));

  return element && isRendered(element) ? element : null;
};

/**
 * Waits until the anchor is rendered, e.g. when it lives in a lazily loaded dashboard module.
 * Resolves to `null` when the timeout elapses first.
 */
export const waitForFeatureHintAnchor = (hintKey: string, timeoutMs: number): Promise<HTMLElement | null> =>
  new Promise(resolve => {
    const found = resolveFeatureHintAnchor(hintKey);
    if (found) {
      resolve(found);
      return;
    }

    const timeoutId = window.setTimeout(() => finish(null), timeoutMs);
    const observer = new MutationObserver(() => {
      const element = resolveFeatureHintAnchor(hintKey);
      if (element) finish(element);
    });

    function finish(element: HTMLElement | null) {
      observer.disconnect();
      window.clearTimeout(timeoutId);
      resolve(element);
    }

    // Only attributes that can reveal the anchor; records are delivered once per microtask, not per mutation.
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "style", "hidden", "open"],
    });
  });
